import React from 'react'
import { Box, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import useCountUp from './hooks/useCountUp'
import { dashboardCardHeadingTypography, dashboardCardValueTypography } from './typography'

export interface DashboardStatCardProps {
  title: string
  titleTooltip?: string
  value: number | null | undefined
  loading: boolean
  error: string | null
  icon: React.ElementType<SvgIconProps>
}

const normalizeValue = (value: number | null | undefined): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0
  }

  const normalized = Math.floor(value)
  return normalized >= 0 ? normalized : 0
}

const DashboardStatCard: React.FC<DashboardStatCardProps> = ({
  title,
  titleTooltip,
  value,
  loading,
  error,
  icon,
}) => {
  const theme = useTheme()
  const IconComponent = icon
  const targetValue = React.useMemo(() => normalizeValue(value), [value])
  const animatedValue = useCountUp(targetValue)
  const formattedValue = React.useMemo(() => Math.round(animatedValue).toLocaleString(), [animatedValue])
  const showError = !loading && Boolean(error)

  const iconBackground = React.useMemo(
    () =>
      alpha(
        theme.palette.primary.main,
        theme.palette.mode === 'light' ? 0.08 : 0.24,
      ),
    [theme],
  )
  const headingStyles = React.useMemo(() => dashboardCardHeadingTypography(theme), [theme])
  const valueStyles = React.useMemo(() => dashboardCardValueTypography(theme), [theme])

  return (
    <Paper
      elevation={6}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: iconBackground,
          color: theme.palette.primary.main,
          fontSize: 28,
        }}
      >
        {loading ? (
          <Skeleton variant="circular" width={28} height={28} />
        ) : (
          <IconComponent fontSize="inherit" color="inherit" />
        )}
      </Box>
      {titleTooltip ? (
        <Tooltip title={titleTooltip} arrow>
          <span style={{ display: 'inline-flex' }}>
            <Typography component="span" sx={headingStyles}>
              {title}
            </Typography>
          </span>
        </Tooltip>
      ) : (
        <Typography component="span" sx={headingStyles}>
          {title}
        </Typography>
      )}
      {loading ? (
        <Skeleton
          variant="text"
          width="55%"
          sx={{
            fontSize: valueStyles.fontSize,
            lineHeight: valueStyles.lineHeight,
            mt: theme.spacing(0.25),
          }}
        />
      ) : showError ? (
        <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>
          {error}
        </Typography>
      ) : (
        <Typography component="p" sx={valueStyles}>
          {formattedValue}
        </Typography>
      )}
    </Paper>
  )
}

export default DashboardStatCard
